/**
 * /dashboard — the hub landing page.
 *
 * What this lands:
 *  - First-run / awareness banners (trial countdown + 2FA enrolment
 *    prompt) above the welcome header so a brand-new tenant sees the
 *    actionable notes before anything else.
 *  - Pinned-announcements ribbon (G11), feature-decorative — silent
 *    on error.
 *  - Welcome PageHeader keyed off /me.
 *  - Accounting hero (G_ACC8): only when the tenant has the
 *    `accounting` feature AND the caller is an accounting-admin.
 *    Empty-state for fresh tenants, snapshot tiles otherwise.
 *  - "My queue" / open-items widget across helpdesk, travel, maternity,
 *    surveys — each row hides on 4xx so feature-gated modules don't
 *    render a dead row.
 *  - "This fortnight" engagement widget (birthdays + work
 *    anniversaries within 14 days).
 *  - First-run setup checklist (HR-admin + trial only): 5 derived items
 *    pointing at Settings → Workspace, People (invite), Settings →
 *    Leave types (configure), POSH (start training), Settings → Billing
 *    (pick a plan). Dismissible per-tenant via localStorage.
 *  - Module quick-jump grid: each tile shows label + 1-line blurb +
 *    arrow CTA, filtered by /me.features (so trial-only tenants don't
 *    see Accounting until they upgrade).
 *
 * The 5 awareness/engagement widgets above the checklist were ported
 * 1:1 from the Next.js sibling (`frontend/src/components/dashboard/*`)
 * with `next/link` swapped for `@tanstack/react-router Link` and the
 * `"use client"` directive dropped.
 */

import { useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  ArrowRight,
  BadgeIndianRupee,
  Briefcase,
  CalendarDays,
  CheckCircle2,
  Circle,
  FolderKanban,
  type LucideIcon,
  Receipt,
  ShieldCheck,
  Target,
  Users,
  X,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/shared/page-header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AccountingHero } from "@/components/dashboard/accounting-hero";
import { AnnouncementsRibbon } from "@/components/dashboard/announcements-ribbon";
import { EngagementWidget } from "@/components/dashboard/engagement-widget";
import { FirstRunBanners } from "@/components/dashboard/first-run-banners";
import { MyOpenItems } from "@/components/dashboard/my-open-items";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/")({
  component: DashboardHub,
});

// ---------------------------------------------------------------------------
// Module cards — quick-jump grid filtered by /me.features + role.
// ---------------------------------------------------------------------------

interface ModuleCard {
  /** Visible label. */
  label: string;
  /** Where the tile links. Must be a real route in the SPA. */
  href: string;
  icon: LucideIcon;
  /** One-line description. Keep < 60 chars so the tile breathes. */
  blurb: string;
  /** Feature flag on /me.features required to surface; omit for always. */
  feature?: string;
  /** Roles allowed to see this tile; omit for any authenticated role. */
  roles?: string[];
}

const HR_ADMINS = ["company_admin", "hr_manager"];

// 2026-06-03 IA refresh: order matches the slim sidebar
// (Time & Leave first, Helpdesk dropped, no Timesheet tile since
// Time & Leave already covers it).
const MODULE_CARDS: ModuleCard[] = [
  {
    label: "Time & Leave",
    href: "/app/$tenantSlug/calendar",
    icon: CalendarDays,
    blurb: "Calendar, leave requests, balances, approvals.",
    feature: "leave_management",
  },
  {
    label: "Expenses",
    href: "/app/$tenantSlug/expenses",
    icon: Receipt,
    blurb: "Submit and approve expenses; reimbursement batches.",
    feature: "expense",
  },
  {
    label: "People",
    href: "/app/$tenantSlug/people",
    icon: Users,
    blurb: "Directory, employees, change requests.",
    roles: [...HR_ADMINS, "manager"],
  },
  {
    label: "HR",
    href: "/app/$tenantSlug/hr",
    icon: FolderKanban,
    blurb: "Onboarding, offboarding, documents, assets.",
    feature: "hr_core",
  },
  {
    label: "Hiring",
    href: "/app/$tenantSlug/hiring",
    icon: Briefcase,
    blurb: "Requisitions, candidates, interviews.",
    feature: "hiring",
  },
  {
    label: "Performance",
    href: "/app/$tenantSlug/performance",
    icon: Target,
    blurb: "Goals, reviews, 1:1s, feedback.",
    feature: "performance",
  },
  {
    label: "Payroll",
    href: "/app/$tenantSlug/payroll",
    icon: BadgeIndianRupee,
    blurb: "Monthly runs, PF/ESI, payslips.",
    roles: HR_ADMINS,
  },
  {
    label: "POSH",
    href: "/app/$tenantSlug/posh",
    icon: ShieldCheck,
    blurb: "Training, ICC, complaints, policy.",
    feature: "posh",
  },
];

// ---------------------------------------------------------------------------
// Setup checklist — derived from /me; HR-admin + trial only.
// ---------------------------------------------------------------------------

interface ChecklistItem {
  id: string;
  label: string;
  description: string;
  href: string;
  /** Heuristic to mark "done" — if true the row shows the checked icon
   *  and renders strikethrough. */
  done: boolean;
}

function DashboardHub() {
  const { user } = useAuth();
  const name = user?.first_name?.trim() || "there";
  const role = user?.role ?? "";
  const features = user?.features ?? [];
  const isHRAdmin = HR_ADMINS.includes(role);
  const isTrial = user?.tenant?.subscription_status === "trial";

  // Per-tenant dismissal: a tenant the user has dismissed once won't
  // see the checklist again on the same browser. Keyed by tenant_id
  // so logging into a different workspace starts fresh.
  const dismissKey = `wr_setup_dismissed_${user?.tenant_id ?? "none"}`;
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(dismissKey) === "1";
  });

  function dismiss() {
    try {
      window.localStorage.setItem(dismissKey, "1");
    } catch {
      // localStorage full / disabled — fail closed, just hide locally
    }
    setDismissed(true);
  }

  const showChecklist = isHRAdmin && isTrial && !dismissed;

  const checklistItems = useMemo<ChecklistItem[]>(() => {
    if (!showChecklist) return [];
    const items: ChecklistItem[] = [
      {
        id: "workspace",
        label: "Set your workspace handle",
        description:
          "Pick the public handle that appears in careers links and statutory exports.",
        href: "/app/$tenantSlug/settings/workspace",
        done: false,
      },
      {
        id: "leave-types",
        label: "Review your leave policy",
        description:
          "Seven default leave types ship with every workspace — tweak the days, naming, or rules.",
        href: "/app/$tenantSlug/settings/leave-types",
        done: false,
      },
      {
        id: "people",
        label: "Invite your team",
        description:
          "Add employees one-by-one or import a CSV from People.",
        href: "/app/$tenantSlug/people",
        done: false,
      },
    ];
    if (features.includes("posh")) {
      items.push({
        id: "posh",
        label: "Set up your POSH committee",
        description:
          "Configure the Internal Complaints Committee (POSH Act §4) before assigning training.",
        href: "/app/$tenantSlug/posh/icc",
        done: false,
      });
    }
    items.push({
      id: "billing",
      label: "Choose a plan when you're ready",
      description:
        "Your trial includes most modules. Activate billing to keep them past the trial window.",
      href: "/app/$tenantSlug/settings/billing",
      done: false,
    });
    return items;
  }, [showChecklist, features]);

  // Filter the module grid against the tenant's features + caller's role.
  const visibleModules = useMemo(() => {
    return MODULE_CARDS.filter((m) => {
      if (m.feature && !features.includes(m.feature)) return false;
      if (m.roles && !m.roles.includes(role)) return false;
      return true;
    });
  }, [features, role]);

  return (
    <div className="space-y-6">
      {/*
        Order is load-bearing:
          1) FirstRunBanners — actionable awareness (trial countdown +
             mandatory-2FA enrolment prompt) above the welcome line so
             a brand-new tenant sees them first.
          2) AnnouncementsRibbon — pinned tenant-wide announcements.
          3) PageHeader — welcome line.
          4) AccountingHero — only when caller is an accounting-admin
             and the tenant has the feature; widget self-hides
             otherwise.
          5) MyOpenItems — "My queue" across helpdesk/travel/maternity/
             surveys. Each row self-hides on 4xx so feature-gated
             modules don't leave a dead row.
          6) EngagementWidget — birthdays + work anniversaries inside
             a 14-day window.
          7) Getting-started checklist (existing, HR-admin + trial
             only).
          8) Module quick-jump grid (existing).
      */}
      <FirstRunBanners />
      <AnnouncementsRibbon />

      <PageHeader
        title={`Welcome, ${name}`}
        description={
          user?.tenant
            ? `You're signed in to ${user.tenant.name}.`
            : "Your WorkRight dashboard."
        }
      />

      <AccountingHero />
      <MyOpenItems />
      <EngagementWidget />

      {showChecklist && checklistItems.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
            <div>
              <CardTitle className="text-base">
                Getting started
              </CardTitle>
              <CardDescription>
                A few quick steps to make your workspace useful. Skip
                anything you don&apos;t need.
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={dismiss}
              aria-label="Dismiss getting-started"
              className="h-7 w-7"
            >
              <X className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="divide-y divide-border">
              {checklistItems.map((item) => (
                <li key={item.id}>
                  <Link
                    to={item.href}
                    className="group flex items-start gap-3 py-3 transition-colors hover:bg-muted/40 sm:px-3 sm:-mx-3 sm:rounded-md"
                  >
                    {item.done ? (
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                    ) : (
                      <Circle className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                    )}
                    <div className="flex-1">
                      <div className="text-sm font-medium text-foreground group-hover:text-primary">
                        {item.label}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {item.description}
                      </div>
                    </div>
                    <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <div>
        <h2 className="mb-3 text-sm font-medium text-muted-foreground">
          Jump into a module
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {visibleModules.map((m) => {
            const Icon = m.icon;
            return (
              <Link
                key={m.href}
                to={m.href}
                className="group rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary/40 hover:bg-accent/40"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium text-foreground group-hover:text-primary">
                        {m.label}
                      </span>
                      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                    </div>
                    <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                      {m.blurb}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
